import { createRoot } from 'react-dom/client'
import { UserManager } from 'oidc-client-ts'
import './index.css'
import App from './App.tsx'
import { oidcConfig } from './config/oidc'

// --- Silent-renew callback handling ---------------------------------------
//
// `oidc-client-ts` runs silent renewal inside a hidden iframe pointed at
// `silent_redirect_uri`. That URL hits Vite's index.html, which would
// normally mount the entire React app — including a second AuthProvider
// instance — inside the iframe. The iframe's AuthProvider then races with
// the silent-renew handler for the `?code=&state=` params on the URL,
// which is exactly the bug we were seeing (silent renew never completing,
// or completing intermittently).
//
// Fix: detect the silent-renew path BEFORE creating the React root. When
// we're inside the iframe we just call `signinSilentCallback()` on a
// fresh UserManager and exit. The call posts the new tokens back to the
// parent window's UserManager via `postMessage`; nothing else needs to
// happen in the iframe.
const SILENT_RENEW_PATH = `${import.meta.env.BASE_URL}silent-renew`

if (window.location.pathname === SILENT_RENEW_PATH) {
  new UserManager(oidcConfig)
    .signinSilentCallback()
    .catch((err: unknown) => {
      // The parent's `accessTokenExpired` / `silentRenewError` events
      // will still fire on timeout, so all this does is surface the
      // failure in DevTools while debugging.
      console.error('[silent-renew] signinSilentCallback failed:', err)
    })
} else {
  // NOTE: React StrictMode is intentionally disabled at the root.
  //
  // FormIO (formiojs v4) registers global document-level handlers and a
  // stateful builder instance on first mount. StrictMode's
  // mount → unmount → mount cycle in dev leaves stale handlers behind
  // because FormIO's `detach` doesn't fully reverse `attach`. The second
  // mount then creates a new builder whose dialog DOM ends up bound to
  // the *first* (now-orphaned) instance — clicks on Save/Cancel/X land
  // on dead listeners and the UI feels frozen.
  //
  // We switched from `@formio/js@5` + `@formio/react@6` to legacy
  // formiojs@4 (mounted directly via DOM refs) to work around an
  // additional v5-specific freeze in the component-edit dialog. Even on
  // v4, the StrictMode double-mount remains hostile to FormIO's
  // lifecycle, so we keep StrictMode off globally until we can wrap the
  // builder in a dedicated mount-once boundary.
  createRoot(document.getElementById('root')!).render(<App />)
}
